import React from 'react';
import { Link } from 'react-router-dom';

import Avatar from '../../Avatar';
import { avatarConfigDisplayColors } from '../../../constants/avatarPalette';
import { useTranslation } from '../../../contexts/TranslationContext';
import { RecommendationFeedItem } from '../../../types/interfaces';
import { translateEvent } from '../../../utils/translateEvent';
import { getTierLabel } from '../../../utils/tierLabel';
import './RecommendationPanel.scss';

export interface RecommendationPanelProps {
  item: RecommendationFeedItem;
  featured?: boolean;
  formattedEvent?: string;
  showGameInfo?: boolean;
}

function formatTime(dt?: string): string {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const CheckSvg: React.FC = () => (
  <svg viewBox="0 0 10 10" width="9" height="9" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2,5.5 4.5,8 8,3" />
  </svg>
);

const RecommendationPanel: React.FC<RecommendationPanelProps> = ({
  item,
  featured = false,
  formattedEvent,
  showGameInfo = false,
}) => {
  const { t } = useTranslation();
  const expertName = item.tipster.full_name?.trim()
    || t.common.expertNumber.replace('{id}', String(item.tipster.id));
  const initials = expertName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
  const matrix = item.tipster.avatar_matrix;
  const showAvatar =
    Array.isArray(matrix) &&
    matrix.length > 0 &&
    item.tipster.avatar_config;
  const eventText = formattedEvent ?? item.bet_event?.event ?? '—';
  const gameTime = formatTime(item.bet_event?.game?.datetime);
  const odds = item.bet_event?.odds;
  const stake = item.stake;

  const tier = item.tipster_tier?.level;
  const tierLabel = getTierLabel(tier, item.tipster_tier?.name, t);
  const tierMod = tier === 0 || tier == null ? 'free' : tier === 1 ? 'pro' : 'elite';

  return (
    <div className={`rec-panel${featured ? ' rec-panel--featured' : ''}`}>
      <div className="rec-panel__top">
        <div className="rec-panel__expert-row">
          <div className="rec-panel__expert-details">
            <Link
              to={`/experts/${item.tipster.id}`}
              className="rec-panel__expert-profile-link"
            >
              <div className="rec-panel__avatar">
                {showAvatar ? (
                  <Avatar
                    matrix={matrix as number[][]}
                    {...avatarConfigDisplayColors(item.tipster.avatar_config)}
                    compact
                    circular
                  />
                ) : (
                  initials
                )}
              </div>
              <div className="rec-panel__name-row">
                <span className="rec-panel__name">{expertName}</span>
                {item.tipster.is_verified && (
                  <span className="rec-panel__verified">
                    <CheckSvg />
                  </span>
                )}
              </div>
            </Link>
            {gameTime && <div className="rec-panel__sub">{gameTime}</div>}
          </div>
          <div className="rec-panel__tier">
            <span className={`rec-panel__tier-badge rec-panel__tier-badge--${tierMod}`}>
              {tierLabel}
            </span>
          </div>
        </div>

        {showGameInfo && item.bet_event?.game && (
          <div className="rec-panel__game-info">
            {item.bet_event.game.league?.name && (
              <span className="rec-panel__game-league">{translateEvent(item.bet_event.game.league.name, t.eventsDictionary)}</span>
            )}
            <span className="rec-panel__game-teams">
              {translateEvent(item.bet_event.game.home_team, t.eventsDictionary)} {t.common.vs} {translateEvent(item.bet_event.game.away_team, t.eventsDictionary)}
            </span>
          </div>
        )}

        <div className="rec-panel__headline">{eventText}</div>

        {item.tipster_description && (
          <p className="rec-panel__desc">{item.tipster_description}</p>
        )}
      </div>

      <div className="rec-panel__bottom">
        {odds != null && (
          <div className="rec-panel__odds-block">
            <span className="rec-panel__odds-val">{odds.toFixed(2)}</span>
            <span className="rec-panel__odds-label">{t.common.odds}</span>
          </div>
        )}
        {stake != null && Number(stake) > 0 && (
          <>
            <div className="rec-panel__divider-v" />
            <span className="rec-panel__stake">
              {t.common.stake} <span className="rec-panel__stake-val">{stake}u</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default RecommendationPanel;
